import { rgbaToPdfColor, hexToRgbArray, isRgba } from '../utils/color';
import { fabricToPdfCoords } from '../utils/coordinateUtils';
import { fabric } from 'fabric';

function getPathPointsInCanvasSpace(pathObject) {
  const segments = Array.isArray(pathObject?.path) ? pathObject.path : [];
  if (!segments.length) return [];

  const matrix = pathObject.calcTransformMatrix();
  const offsetX = pathObject.pathOffset?.x || 0;
  const offsetY = pathObject.pathOffset?.y || 0;

  let currentX = 0;
  let currentY = 0;
  let subpathStartX = 0;
  let subpathStartY = 0;

  const points = [];

  const pushPoint = (x, y) => {
    const transformed = fabric.util.transformPoint(
      new fabric.Point(x - offsetX, y - offsetY),
      matrix
    );

    const lastPoint = points[points.length - 1];
    if (lastPoint && lastPoint.x === transformed.x && lastPoint.y === transformed.y) {
      return;
    }

    points.push({
      x: transformed.x,
      y: transformed.y,
    });
  };

  const resolveX = (value, isRelative) => (isRelative ? currentX + value : value);
  const resolveY = (value, isRelative) => (isRelative ? currentY + value : value);

  segments.forEach(([rawCommand, ...coords]) => {
    if (!rawCommand) return;

    const command = String(rawCommand);
    const isRelative = command === command.toLowerCase();
    const normalized = command.toUpperCase();

    switch (normalized) {
      case 'M': {
        const x = resolveX(coords[0], isRelative);
        const y = resolveY(coords[1], isRelative);
        currentX = x;
        currentY = y;
        subpathStartX = x;
        subpathStartY = y;
        pushPoint(x, y);
        break;
      }
      case 'L': {
        const x = resolveX(coords[0], isRelative);
        const y = resolveY(coords[1], isRelative);
        currentX = x;
        currentY = y;
        pushPoint(x, y);
        break;
      }
      case 'H': {
        const x = resolveX(coords[0], isRelative);
        currentX = x;
        pushPoint(currentX, currentY);
        break;
      }
      case 'V': {
        const y = resolveY(coords[0], isRelative);
        currentY = y;
        pushPoint(currentX, currentY);
        break;
      }
      case 'Q': {
        const cx = resolveX(coords[0], isRelative);
        const cy = resolveY(coords[1], isRelative);
        const x = resolveX(coords[2], isRelative);
        const y = resolveY(coords[3], isRelative);
        pushPoint(cx, cy);
        pushPoint(x, y);
        currentX = x;
        currentY = y;
        break;
      }
      case 'T': {
        const x = resolveX(coords[0], isRelative);
        const y = resolveY(coords[1], isRelative);
        pushPoint(x, y);
        currentX = x;
        currentY = y;
        break;
      }
      case 'C': {
        const c1x = resolveX(coords[0], isRelative);
        const c1y = resolveY(coords[1], isRelative);
        const c2x = resolveX(coords[2], isRelative);
        const c2y = resolveY(coords[3], isRelative);
        const x = resolveX(coords[4], isRelative);
        const y = resolveY(coords[5], isRelative);
        pushPoint(c1x, c1y);
        pushPoint(c2x, c2y);
        pushPoint(x, y);
        currentX = x;
        currentY = y;
        break;
      }
      case 'S': {
        const c2x = resolveX(coords[0], isRelative);
        const c2y = resolveY(coords[1], isRelative);
        const x = resolveX(coords[2], isRelative);
        const y = resolveY(coords[3], isRelative);
        pushPoint(c2x, c2y);
        pushPoint(x, y);
        currentX = x;
        currentY = y;
        break;
      }
      case 'A': {
        const x = resolveX(coords[5], isRelative);
        const y = resolveY(coords[6], isRelative);
        pushPoint(x, y);
        currentX = x;
        currentY = y;
        break;
      }
      case 'Z': {
        currentX = subpathStartX;
        currentY = subpathStartY;
        pushPoint(currentX, currentY);
        break;
      }
      default:
        break;
    }
  });

  return points;
}

/**
 * Convert Fabric freehand path to PDF Ink annotation
 */
export function fabricPathToPdfInk({
  page,
  fabricObject,
  basePageDimensions: { width, height },
  user_full_name,
}) {
  const { strokeWidth, stroke } = fabricObject;
  const points = getPathPointsInCanvasSpace(fabricObject);

  if (!points.length) return;

  const pathPoints = points.flatMap(({ x, y }) => {
    const pdfCoord = fabricToPdfCoords({
      x1: x,
      y1: y,
      x2: x,
      y2: y,
      page,
      width,
      height,
    });
    return [pdfCoord.x1, pdfCoord.y1];
  });

  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);

  let C, CA;

  if (isRgba(stroke)) {
    ({ C, CA } = rgbaToPdfColor(stroke));
  } else {
    C = hexToRgbArray(stroke);
    CA = 1;
  }

  page.node.addAnnot(
    page.doc.context.obj({
      Type: 'Annot',
      Subtype: 'Ink',
      Rect: fabricToPdfCoords({
        x1: Math.min(...xs),
        y1: Math.min(...ys),
        x2: Math.max(...xs),
        y2: Math.max(...ys),
        page,
        width,
        height,
      }).rect,
      InkList: [pathPoints],
      Border: [0, 0, strokeWidth],
      C,
      CA,
      T: user_full_name,
    })
  );
}
